import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Avatar,
  Button,
  CssBaseline,
  TextField,
  Link,
  Paper,
  Box,
  Grid,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  Alert,
} from "@mui/material";
import {
  LockOutlined as LockOutlinedIcon,
  Visibility,
  VisibilityOff,
  AdminPanelSettings,
} from "@mui/icons-material";
import { indigo } from "@mui/material/colors";
import { Link as RouterLink } from "react-router-dom";
import adminAuthBg from "../../images/bg-img.png";
import useAdminContext from "../../store/contexts/AdminContext";
import Copyright from "../../components/Copyright/Copyright";
import { ADMIN_ROUTES } from "../../routes/Admin/AdminPaths";

export default function AdminResetPassword() {
  const {
    authState: { loading, error, message },
    resetPasswordAdminApiCall,
    clearErrors,
  } = useAdminContext();

  const history = useHistory();
  const location = useLocation();

  // Extract token from URL query params
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    confirm_password: "",
    token: token || "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Clear any previous errors when component mounts
    clearErrors();

    // Redirect if no token is provided
    if (!token) {
      history.push(ADMIN_ROUTES.forgotPassword);
    }
  }, [clearErrors, history, token]);

  useEffect(() => {
    // Show success message if API returns success
    if (message) {
      setSuccess(true);
    }
  }, [message]);

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear field-specific error when user types
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    }

    if (!formData.confirm_password) {
      errors.confirm_password = "Please confirm your password";
    } else if (formData.password !== formData.confirm_password) {
      errors.confirm_password = "Passwords do not match";
    }

    if (!formData.token) {
      errors.token = "Reset token is missing";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (validateForm()) {
      resetPasswordAdminApiCall(formData);
    }
  };

  return (
    <Grid container component="main" sx={{ height: "100vh" }}>
      <CssBaseline />
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          backgroundImage: `url(${adminAuthBg})`,
          backgroundRepeat: "no-repeat",
          backgroundColor: (t) =>
            t.palette.mode === "light"
              ? t.palette.grey[50]
              : t.palette.grey[900],
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <Box
          sx={{
            my: 8,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: indigo[700], width: 56, height: 56 }}>
            <AdminPanelSettings fontSize="large" />
          </Avatar>
          <Typography
            component="h1"
            variant="h5"
            sx={{ mt: 2, fontWeight: 500 }}
          >
            Set New Password
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
              {error}
            </Alert>
          )}

          {success ? (
            <Box sx={{ mt: 3, textAlign: "center", width: "100%" }}>
              <Alert severity="success" sx={{ mb: 3 }}>
                {message || "Your password has been successfully reset!"}
              </Alert>
              <Typography variant="body2" sx={{ mb: 3 }}>
                You can now login with your new password.
              </Typography>
              <Button
                component={RouterLink}
                to={ADMIN_ROUTES.login}
                variant="contained"
                color="primary"
                sx={{
                  mt: 2,
                  backgroundColor: indigo[700],
                  "&:hover": {
                    backgroundColor: indigo[800],
                  },
                }}
              >
                Go to Login
              </Button>
            </Box>
          ) : (
            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit}
              sx={{ mt: 3, width: "100%" }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="New Password"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                error={!!formErrors.password}
                helperText={formErrors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="confirm_password"
                label="Confirm New Password"
                type={showConfirmPassword ? "text" : "password"}
                id="confirm_password"
                autoComplete="new-password"
                value={formData.confirm_password}
                onChange={handleChange}
                error={!!formErrors.confirm_password}
                helperText={formErrors.confirm_password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={handleShowConfirmPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.2,
                  backgroundColor: indigo[700],
                  "&:hover": {
                    backgroundColor: indigo[800],
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Reset Password"
                )}
              </Button>

              <Grid container justifyContent="center">
                <Grid item>
                  <Link
                    component={RouterLink}
                    to={ADMIN_ROUTES.login}
                    variant="body2"
                    color="primary"
                  >
                    Back to Login
                  </Link>
                </Grid>
              </Grid>

              <Copyright sx={{ mt: 5 }} />
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
}
